import { useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { RefreshCw, TrendingUp } from "lucide-react";
import { useOperationOverview } from "../hooks/useOperationOverview";
import { usePollingResource } from "../hooks/usePollingResource";
import type { OperationOverview, OperationPerformance, TradesResponse } from "../api/types";
import { fmtKst, fmtNumber, fmtPct, fmtDuration } from "../lib/format";
import { PageHeader, StatTile, EmptyState } from "../components/ui/Primitives";
import CockpitTabs from "../components/CockpitTabs";
import ResourceNotice from "../components/ResourceNotice";
import Invest from "./Invest";
import EquityChart from "../components/OperationEquityChart";
import StrategyMonitor from "../components/StrategyMonitor";
const tabs = [{ id: "monitor", label: "전략·종목" }, { id: "performance", label: "성과" }, { id: "positions", label: "포지션" },
    { id: "trades", label: "거래" }, { id: "invest", label: "ETF 투자북" }];
const money = (n: number | null | undefined) => n == null ? "—" : `${fmtNumber(n, { digits: 2, sign: true })} USDT`;
export default function Operation() {
    const { tab } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const active = tabs.some(t => t.id === tab) ? tab! : "monitor";
    const resource = useOperationOverview(active !== "invest");
    const [revision, setRevision] = useState(0);
    const refresh = () => { resource.refresh(); setRevision(v => v + 1); };
    return <div className="cockpit-page">
    <PageHeader icon={<TrendingUp size={22}/>} title="운용" description="C1 PAPER · 비용 후 평가 자산과 완료 거래" actions={<button className="btn-secondary" onClick={refresh}><RefreshCw size={14}/>새로고침</button>}/>
    {active !== "invest" && <ResourceNotice {...resource}/>}
    <CockpitTabs id="operation" tabs={tabs} value={active} onChange={value => navigate(`/operation/${value}${location.search}${location.hash}`)}/>
    <section id="operation-panel" role="tabpanel" aria-labelledby={`operation-${active}`}>
      {active === "monitor" && <StrategyMonitor overview={resource.overview} revision={revision} executionFailed={!!resource.error}/>}
      {active === "performance" && <PerformanceTab revision={revision}/>}
      {active === "positions" && <PositionsTab overview={resource.overview} revision={revision} error={resource.error}/>}
      {active === "trades" && <TradesTab revision={revision}/>}
      {active === "invest" && <Invest />}
    </section>
  </div>;
}
function PerformanceTab({ revision }: {
    revision: number;
}) {
    const [params, setParams] = useSearchParams();
    const selected = params.get("period");
    const period = selected && ["24h", "7d", "30d"].includes(selected) ? selected : "all";
    const resource = usePollingResource<OperationPerformance>(`/operation/performance?period=${period}&scope=C1&_refresh=${revision}`, 15000);
    const data = resource.data;
    const change = (p: string) => { const next = new URLSearchParams(params); next.set("period", p); setParams(next); };
    return <div className="cockpit-page">
    <ResourceNotice {...resource}/>
    {data?.warning && <div className="notice-card" role="status">{data.warning}</div>}
    {data?.stale && <p className="research-alert">최근 관측이 오래된 자료입니다. 관측 시각을 확인해 주세요.</p>}
    <div className="grid grid-4">
      <StatTile label="기간 순손익" value={money(data?.net_pnl)}/>
      <StatTile label="기간 수익률" value={fmtPct(data?.return_pct, { sign: true })}/>
      <StatTile label="최대 낙폭 (MDD)" value={fmtPct(data?.mdd_pct)} sub={data?.coverage?.complete === false ? "관측 누락 · 확인 필요" : "실제 관측 기준"}/>
      <StatTile label="완료 거래" value={data?.closed_trades != null ? `${data.closed_trades}건` : "—"}/>
    </div>
    <section className="card">
      <div className="card-title-row"><h3 className="card-title">C1 자산 추이</h3>
        <span className="card-title-meta">최근 평가 자산 {money(data?.current_equity)}</span></div>
      <div className="btn-row" role="group" aria-label="성과 기간">
        {[['all', '운용 시작 이후'], ['30d', '30일'], ['7d', '7일'], ['24h', '24시간']].map(([id, label]) => <button key={id} className="btn-secondary" aria-pressed={period === id} onClick={() => change(id)}>{label}</button>)}
      </div>
      <EquityChart points={data?.equity_curve ?? []}/>
      <p className="muted-note">계산 기준 자산 {money(data?.initial_equity)} · 실제 관측 {fmtKst(data?.observation_range?.start)} ~ {fmtKst(data?.observation_range?.end)} KST</p>
      <p className="muted-note">요청 시작 {fmtKst(data?.requested_start)} KST · 시각이 맞는 관측 {data?.coverage?.aligned_observations ?? "—"}개.
        기록이 짧으면 표시한 실제 구간만 계산합니다. 외부 자금 이동 근거가 없으면 수익률은 확인 필요입니다.</p>
    </section>
  </div>;
}
interface SnapshotRow {
    ts: string;
    account_id: string;
    equity: number | null;
    unrealized: number | null;
}
function PositionsTab({ overview, revision, error }: {
    overview: OperationOverview | null;
    revision: number;
    error: string | null;
}) {
    const positions = overview?.positions ?? [];
    const confirmed = overview?.positions_confirmed === true && !error;
    return <div className="cockpit-page">
    <section className="card">
      <div className="card-title-row"><h3 className="card-title">C1 포지션</h3><span className="card-title-meta">관측 {fmtKst(overview?.observed_at)} KST</span></div>
      {!confirmed && <p className="research-alert">현재 포지션 확인 필요. 아래 값은 마지막 관측입니다.</p>}
      {positions.length === 0 ? <EmptyState title={confirmed ? "포지션 없음" : "포지션 관측 대기"} description={overview?.status_message}/> :
            <div className="table-wrap"><table className="position-table"><thead><tr><th>종목·계정</th><th>방향</th><th>수량</th><th>진입가</th><th>현재가</th><th>미실현 손익</th><th>손절가</th><th>노출</th></tr></thead>
          <tbody>{positions.map(p => <tr key={p.account_id}>
            <td data-label="종목·계정"><strong>{p.symbol}</strong><p className="muted-note">{p.account_id}</p></td>
            <td data-label="방향">{p.side}</td><td data-label="수량">{fmtNumber(p.qty, { digits: 6 })}</td>
            <td data-label="진입가">{fmtNumber(p.entry_price, { digits: 2 })}</td>
            <td data-label="현재가">{fmtNumber(p.current_price, { digits: 2 })}</td>
            <td data-label="미실현 손익">{money(p.unrealized_pnl)}</td>
            <td data-label="손절가">{fmtNumber(p.stop_price, { digits: 2 })}</td>
            <td data-label="노출">{p.exposure != null ? `${p.exposure}배` : '—'}</td>
          </tr>)}</tbody></table></div>}
      <p className="monitor-rule">C1 · 확정 4시간봉 EMA(50/150) 교차 · 종목별 노출 {overview?.strategy_params.exposure ?? "—"}배 · 반대 교차 또는 6 ATR 손절</p>
    </section>
    <SnapshotHistory revision={revision}/>
  </div>;
}
function SnapshotHistory({ revision }: {
    revision: number;
}) {
    const [open, setOpen] = useState(false);
    const resource = usePollingResource<{
        snapshots: SnapshotRow[];
    }>(open ? `/positions/history?hours=24&account_scope=C1&_refresh=${revision}` : null, 15000);
    return <details className="card" onToggle={e => setOpen(e.currentTarget.open)}><summary>C1 최근 계정 스냅샷</summary>
    {open && <><ResourceNotice {...resource}/>
      {!resource.data ? null : resource.data.snapshots.length === 0 ? <EmptyState title="기록 없음"/> : <div className="table-wrap"><table><thead><tr><th>시각 · KST</th><th>계정</th><th>평가 자산</th><th>미실현 손익</th></tr></thead><tbody>{resource.data.snapshots.slice(0, 50).map(row => <tr key={`${row.account_id}:${row.ts}`}><td>{fmtKst(row.ts)}</td><td>{row.account_id}</td><td>{money(row.equity)}</td><td>{money(row.unrealized)}</td></tr>)}</tbody></table></div>}
    </>}
  </details>;
}
function TradesTab({ revision }: {
    revision: number;
}) {
    const [params, setParams] = useSearchParams();
    const page = Math.max(1, Number(params.get("page")) || 1);
    const hours = params.get("hours") ?? "168";
    const all = params.get("period") === "all" || Number(hours) > 720;
    const symbol = params.get("symbol") ?? "";
    const scope = params.get("account_scope") === "archived" ? "archived" : "C1";
    const query = new URLSearchParams({ page: String(page), page_size: "30", time_basis: "close", status: "closed", account_scope: scope, _refresh: String(revision) });
    if (all)
        query.set("period", "all");
    else
        query.set("hours", ["24", "168", "720"].includes(hours) ? hours : "168");
    for (const key of ["symbol", "side", "strategy"]) {
        const val = params.get(key);
        if (val)
            query.set(key, val);
    }
    const resource = usePollingResource<TradesResponse>(`/trades?${query}`, 15000);
    const data = resource.data;
    const setFilter = (key: string, value: string) => {
        const next = new URLSearchParams(params);
        if (value)
            next.set(key, value);
        else
            next.delete(key);
        if (key !== "page")
            next.set("page", "1");
        setParams(next);
    };
    const period = (value: string) => { const next = new URLSearchParams(params); next.set("page", "1"); next.delete("period"); if (value === "all") {
        next.set("period", "all");
        next.delete("hours");
    }
    else
        next.set("hours", value); setParams(next); };
    return <div className="cockpit-page">
    <div className="card filter-row">
      <label>계정 범위<select value={scope} onChange={e => setFilter('account_scope', e.target.value)}><option value="C1">활성 C1</option><option value="archived">종료한 PAPER 계정</option></select></label>
      <label>기간<select value={all ? 'all' : hours} onChange={e => period(e.target.value)}><option value="24">24시간</option><option value="168">7일</option><option value="720">30일</option><option value="all">전체 기록</option></select></label>
      <label>종목<select value={symbol} onChange={e => setFilter('symbol', e.target.value)}><option value="">전체</option>{["BTCUSDT", "ETHUSDT", "SOLUSDT", ...(scope === 'archived' ? ['SOXLUSDT', 'KORUUSDT', 'SKHYUSDT'] : [])].map(s => <option key={s}>{s}</option>)}</select></label>
      <div className="filter-row"><label>방향<select value={params.get('side') ?? ''} onChange={e => setFilter('side', e.target.value)}><option value="">전체</option><option>LONG</option><option>SHORT</option></select></label><label>전략<input value={params.get('strategy') ?? ''} onChange={e => setFilter('strategy', e.target.value)}/></label></div>
    </div>
    <ResourceNotice {...resource}/>
    <section className="card"><div className="card-title-row"><h3 className="card-title">완료 거래 · {scope === 'C1' ? '활성 C1' : '종료 계정'}</h3><span className="card-title-meta">청산 시각 기준 · {data?.total ?? '—'}건</span></div>
      {!data ? <EmptyState title={resource.error ? '거래 조회 확인 필요' : '거래 조회 중'}/> : data.trades.length === 0 ? <EmptyState title="조건에 맞는 완료 거래 없음"/> :
            <div className="table-wrap"><table className="trade-table"><thead><tr><th>청산 시각 · KST</th><th>종목</th><th>방향</th><th>비용 후 손익</th><th>청산 사유</th><th>보유 시간</th></tr></thead>
          <tbody>{data.trades.map(t => <tr key={t.trade_id ?? `${t.signal_id}:${t.ts_open}`}><td data-label="청산 시각 · KST">{fmtKst(t.ts_close)}</td><td data-label="종목·가격·비용"><strong>{t.symbol}</strong><p className="muted-note">계정 {t.account_id ?? '—'}<br />진입 {fmtKst(t.ts_open)}<br />진입가 {fmtNumber(t.entry_px, { digits: 2 })}<br />청산가 {fmtNumber(t.exit_px, { digits: 2 })}<br />수량 {fmtNumber(t.qty, { digits: 6 })}<br />수수료 {money(t.fee)} · 펀딩 {money(t.funding_pnl)}</p></td><td data-label="방향">{t.side}</td><td data-label="비용 후 손익" className={t.pnl_net != null && t.pnl_net < 0 ? 'pnl-negative' : t.pnl_net != null && t.pnl_net > 0 ? 'pnl-positive' : ''}>{money(t.pnl_net)}</td><td data-label="청산 사유">{t.exit_reason ?? '—'}</td><td data-label="보유 시간">{t.ts_close ? fmtDuration((Date.parse(t.ts_close) - Date.parse(t.ts_open)) / 1000) : '—'}</td></tr>)}</tbody></table></div>}
      <div className="btn-row"><button className="btn-secondary" disabled={page <= 1} onClick={() => setFilter('page', String(page - 1))}>이전</button><span>{page} / {data?.total_pages || 1}</span><button className="btn-secondary" disabled={!data || page >= data.total_pages} onClick={() => setFilter('page', String(page + 1))}>다음</button></div>
    </section>
  </div>;
}
